import * as React from "react";
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Checkbox,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControlLabel,
  LinearProgress,
  MenuItem,
  Paper,
  Stack,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Tabs,
  TextField,
  Typography,
} from "@mui/material";
import { alpha } from "@mui/material/styles";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import SavingsOutlinedIcon from "@mui/icons-material/SavingsOutlined";
import SupportAgentOutlinedIcon from "@mui/icons-material/SupportAgentOutlined";
import BusinessOutlinedIcon from "@mui/icons-material/BusinessOutlined";
import GroupsOutlinedIcon from "@mui/icons-material/GroupsOutlined";
import { Link as RouterLink } from "react-router-dom";
import { useMutation, useQuery } from "@apollo/client";
import { UserContext } from "../../../../platform/components/auth/auth-context";
import {
  REASSIGN_CONCIERGE_BROADCAST_GROUP,
  REASSIGN_CONCIERGE_CASE,
  TRANSFER_CONCIERGE_BROADCAST_GROUP,
  TRANSFER_CONCIERGE_CASE,
  UNCLAIM_CONCIERGE_BROADCAST_GROUP,
  UNCLAIM_CONCIERGE_CASE,
} from "../../../../platform/graphql/mutations/care";
import {
  CREATE_AGENT_USER,
  REVIEW_AGENT_APPLICATION,
  SET_AGENT_USER_ACTIVE,
  SET_AGENT_USER_PLATFORM_ADMIN,
} from "../../../../platform/graphql/mutations/user";
import {
  BUSINESS_LICENSE,
  GET_CONCIERGE_BROADCAST_GROUPS,
  GET_CONCIERGE_LEADS,
  GET_INSTALLMENTS_BY_LEAD,
} from "../../../../platform/graphql/queries/care";
import { GET_BUSINESSES } from "../../../../platform/graphql/queries/business";
import { GET_ALL_COUNTRIES } from "../../../../platform/graphql/queries/countryStateCity";
import { GET_BILLING_SUBSCRIPTION } from "../../../../platform/graphql/queries/generic";
import { GET_AGENT_APPLICATIONS, GET_AGENT_USERS } from "../../../../platform/graphql/queries/user";
import {
  AgentApplicationRecord,
  AgentRecord,
  AssignmentOverride,
  DEFAULT_PRIVILEGES,
  buildAgentDetails,
  findAgentRecord,
  formatDate,
  getAgentDisplayLabel,
  getAgentFromLead,
  getAgentLocationLabel as getSharedAgentLocationLabel,
  getAttributionMeta as getSharedAttributionMeta,
  getCaseHistory,
  getConciergeStateFromGroup,
  getConciergeStateFromLead,
  getRoutingLabel as getSharedRoutingLabel,
  getScreeningConcierge,
  hasResolvedAgentIdentity,
  isAgentProfileComplete,
  isAssignableAgentUser,
  isVisibleAgentTabUser,
  mergeAgent,
  normalizeText,
  parseObject,
} from "./conciergeAdminShared";

function getAgentLocationLabel(agent: AgentRecord | null | undefined) {
  return getSharedAgentLocationLabel(agent);
}

function getAttributionMeta(raw: any) {
  return getSharedAttributionMeta(raw);
}

function getRoutingLabel(args: {
  ownerAgentId?: string | null;
  assignmentCount?: number;
  intakeRoutingStatus?: string | null;
  routingMode?: string | null;
}) {
  return getSharedRoutingLabel(args);
}

function StatusChip({
  label,
  color = "default",
}: {
  label: string;
  color?: "default" | "success" | "warning" | "error" | "info";
}) {
  return <Chip size="small" variant="outlined" color={color} label={label} />;
}

function MetricCard({
  title,
  value,
  subtitle,
  icon,
}: {
  title: string;
  value: string | number;
  subtitle: string;
  icon: React.ReactNode;
}) {
  return (
    <Card
      variant="outlined"
      sx={{
        borderRadius: 3,
        height: "100%",
        background: (theme) =>
          `linear-gradient(180deg, ${alpha(theme.palette.primary.main, 0.06)}, ${alpha(
            theme.palette.background.paper,
            0.98,
          )})`,
      }}
    >
      <CardContent>
        <Stack direction="row" justifyContent="space-between" spacing={2}>
          <Box>
            <Typography variant="overline" color="text.secondary">
              {title}
            </Typography>
            <Typography variant="h4" fontWeight={700}>
              {value}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {subtitle}
            </Typography>
          </Box>
          <Box sx={{ color: "primary.main" }}>{icon}</Box>
        </Stack>
      </CardContent>
    </Card>
  );
}

function ResponsiveTableArea({
  children,
  minWidth,
}: {
  children: React.ReactNode;
  minWidth: number;
}) {
  return (
    <Box
      sx={{
        width: "100%",
        overflowX: "auto",
        overflowY: "hidden",
        WebkitOverflowScrolling: "touch",
        "& table": {
          minWidth,
        },
      }}
    >
      {children}
    </Box>
  );
}

function BusinessClaimStatusCell({ businessId }: { businessId: number | string }) {
  const { data, loading } = useQuery(BUSINESS_LICENSE, {
    variables: { businessId: String(businessId) },
    skip: !businessId,
    fetchPolicy: "cache-first",
  });

  if (loading) return <StatusChip label="Checking..." />;
  const license = data?.businessLicense;
  if (!license) return <StatusChip label="Unknown" />;
  return license.claimed ? (
    <StatusChip label="Claimed" color="success" />
  ) : (
    <StatusChip label="Unclaimed" color="warning" />
  );
}

function BusinessSubscriptionCell({ businessId }: { businessId: number | string }) {
  const { data, loading } = useQuery(GET_BILLING_SUBSCRIPTION, {
    variables: { businessId: String(businessId) },
    skip: !businessId,
    fetchPolicy: "cache-first",
  });

  if (loading) return <Typography variant="body2">Checking...</Typography>;
  const subscription = data?.getBillingSubscription;
  if (!subscription?.id) return <StatusChip label="No subscription" />;

  const status = normalizeText(subscription.status).toLowerCase();
  const color =
    status === "active" || status === "trialing"
      ? "success"
      : status === "canceling"
        ? "warning"
        : "default";

  return (
    <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap">
      <StatusChip label={subscription.status || "Unknown"} color={color as any} />
      <StatusChip
        label={`Seats ${Number(subscription.residentCount || 0)}/${Number(
          subscription.residentSeats || 0,
        )}`}
      />
    </Stack>
  );
}

function LeadBillingCell({ lead }: { lead: any }) {
  const { data, loading } = useQuery(GET_INSTALLMENTS_BY_LEAD, {
    variables: { leadId: String(lead?.id || "") },
    skip: !lead?.id,
    fetchPolicy: "cache-first",
  });

  if (loading) return <Typography variant="body2">Checking...</Typography>;

  const installments = data?.referralInstallments ?? [];
  if (!installments.length) return <StatusChip label="No lead invoice schedule" />;

  const now = Date.now();
  const overdue = installments.filter((row: any) => {
    const dueTs = Date.parse(String(row?.dueDate || ""));
    const isOpen = !["PAID", "CANCELLED"].includes(
      String(row?.status || "").toUpperCase(),
    );
    return Number.isFinite(dueTs) && dueTs < now && isOpen;
  });
  const paidCount = installments.filter(
    (row: any) => String(row?.status || "").toUpperCase() === "PAID",
  ).length;
  const nextDue = [...installments]
    .map((row: any) => row?.dueDate)
    .filter(Boolean)
    .map((v: string) => Date.parse(v))
    .filter((ts: number) => Number.isFinite(ts) && ts >= now)
    .sort((a: number, b: number) => a - b)[0];

  return (
    <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap">
      {overdue.length ? (
        <StatusChip label={`${overdue.length} overdue`} color="error" />
      ) : paidCount === installments.length ? (
        <StatusChip label="Paid" color="success" />
      ) : (
        <StatusChip label="In progress" color="warning" />
      )}
      <StatusChip label={`${paidCount}/${installments.length} paid`} />
      {nextDue ? <StatusChip label={`Next ${formatDate(nextDue)}`} /> : null}
    </Stack>
  );
}

export default function ConciergeAdminDashboardPage() {
  const { providerUser } = React.useContext(UserContext);
  const canManageAgents = providerUser?.isPlatformAdmin === true;
  const canViewDashboard =
    Number(providerUser?.userId || 0) > 0 && providerUser?.isPlatformAdmin === true;
  const [activeTab, setActiveTab] = React.useState(0);
  const [selectedAgent, setSelectedAgent] = React.useState<AgentRecord | null>(null);
  const [actionError, setActionError] = React.useState<string | null>(null);
  const [actionInfo, setActionInfo] = React.useState<string | null>(null);
  const [assignmentDialog, setAssignmentDialog] = React.useState<any | null>(null);
  const [assignmentTargetId, setAssignmentTargetId] = React.useState("");
  const [assignmentNote, setAssignmentNote] = React.useState("");
  const [applicationDialog, setApplicationDialog] = React.useState<{
    application: AgentApplicationRecord;
    decision: "APPROVED" | "REJECTED";
  } | null>(null);
  const [applicationReviewNotes, setApplicationReviewNotes] = React.useState("");
  const [applicationTempPassword, setApplicationTempPassword] = React.useState("");
  const [historyDialog, setHistoryDialog] = React.useState<any | null>(null);
  const [queueAgentFilter, setQueueAgentFilter] = React.useState("all");
  const [queueSourceFilter, setQueueSourceFilter] = React.useState("all");
  const [agentFilterCity, setAgentFilterCity] = React.useState("");
  const [agentFilterState, setAgentFilterState] = React.useState("");
  const [agentFilterZip, setAgentFilterZip] = React.useState("");
  const [leadAssignmentOverrides, setLeadAssignmentOverrides] = React.useState<
    Record<string, AssignmentOverride>
  >({});
  const [groupAssignmentOverrides, setGroupAssignmentOverrides] = React.useState<
    Record<string, AssignmentOverride>
  >({});
  const [addAgentOpen, setAddAgentOpen] = React.useState(false);
  const [agentStatusConfirm, setAgentStatusConfirm] = React.useState<AgentRecord | null>(null);
  const [newAgentName, setNewAgentName] = React.useState("");
  const [newAgentEmail, setNewAgentEmail] = React.useState("");
  const [newAgentPhone, setNewAgentPhone] = React.useState("");
  const [newAgentAddress, setNewAgentAddress] = React.useState("");
  const [newAgentCity, setNewAgentCity] = React.useState("");
  const [newAgentState, setNewAgentState] = React.useState("");
  const [newAgentCountry, setNewAgentCountry] = React.useState("");
  const [newAgentZipcode, setNewAgentZipcode] = React.useState("");
  const [newAgentPassword, setNewAgentPassword] = React.useState("");
  const [newAgentAdmin, setNewAgentAdmin] = React.useState(false);
  const shouldLoadApplications = canManageAgents && activeTab === 2;
  const shouldLoadSubscribers = canViewDashboard && activeTab === 3;
  const shouldLoadAgentLocations =
    canViewDashboard && (activeTab === 1 || addAgentOpen);

  const closeAddAgentDialog = React.useCallback(() => {
    setAddAgentOpen(false);
    setNewAgentName("");
    setNewAgentEmail("");
    setNewAgentPhone("");
    setNewAgentAddress("");
    setNewAgentCity("");
    setNewAgentState("");
    setNewAgentCountry("");
    setNewAgentZipcode("");
    setNewAgentPassword("");
    setNewAgentAdmin(false);
  }, []);

  const leadsQuery = useQuery(GET_CONCIERGE_LEADS, {
    variables: { cityCodes: null, statuses: null, limit: 300, offset: 0 },
    skip: !canViewDashboard,
    fetchPolicy: "cache-and-network",
  });
  const screeningQuery = useQuery(GET_CONCIERGE_BROADCAST_GROUPS, {
    variables: { limit: 300, offset: 0 },
    skip: !canViewDashboard,
    fetchPolicy: "cache-and-network",
  });
  const agentUsersQuery = useQuery(GET_AGENT_USERS, {
    variables: { includeInactive: true },
    skip: !canViewDashboard,
    fetchPolicy: "cache-and-network",
  });
  const agentApplicationsQuery = useQuery(GET_AGENT_APPLICATIONS, {
    variables: { statuses: ["PENDING", "APPROVED", "REJECTED"], limit: 200, offset: 0 },
    skip: !shouldLoadApplications,
    fetchPolicy: "cache-and-network",
  });
  const locationQuery = useQuery(GET_ALL_COUNTRIES, {
    skip: !shouldLoadAgentLocations,
    fetchPolicy: "cache-and-network",
  });
  const businessesQuery = useQuery(GET_BUSINESSES, {
    variables: { countryId: null },
    skip: !shouldLoadSubscribers,
    fetchPolicy: "cache-and-network",
  });
  const [transferCase, { loading: transferringCase }] = useMutation(
    TRANSFER_CONCIERGE_CASE,
    {
      onError: (err) => setActionError(err.message || "Could not transfer case."),
      onCompleted: (resp) => {
        if (resp?.transferConciergeCase?.success === false) {
          setActionError(resp.transferConciergeCase.message || "Could not transfer case.");
        }
        leadsQuery.refetch();
      },
    },
  );
  const [reassignCase, { loading: reassigningCase }] = useMutation(
    REASSIGN_CONCIERGE_CASE,
    {
      onError: (err) => setActionError(err.message || "Could not reassign case."),
      onCompleted: (resp) => {
        if (resp?.reassignConciergeCase?.success === false) {
          setActionError(resp.reassignConciergeCase.message || "Could not reassign case.");
        }
        leadsQuery.refetch();
      },
    },
  );
  const [transferBroadcastGroup, { loading: transferringGroup }] = useMutation(
    TRANSFER_CONCIERGE_BROADCAST_GROUP,
    {
      onError: (err) =>
        setActionError(err.message || "Could not transfer screening request."),
      onCompleted: (resp) => {
        if (resp?.transferConciergeBroadcastGroup?.success === false) {
          setActionError(
            resp.transferConciergeBroadcastGroup.message ||
            "Could not transfer screening request.",
          );
        }
        screeningQuery.refetch();
      },
    },
  );
  const [reassignBroadcastGroup, { loading: reassigningGroup }] = useMutation(
    REASSIGN_CONCIERGE_BROADCAST_GROUP,
    {
      onError: (err) =>
        setActionError(err.message || "Could not reassign screening request."),
      onCompleted: (resp) => {
        if (resp?.reassignConciergeBroadcastGroup?.success === false) {
          setActionError(
            resp.reassignConciergeBroadcastGroup.message ||
            "Could not reassign screening request.",
          );
        }
        screeningQuery.refetch();
      },
    },
  );

  const countries = React.useMemo(
    () => locationQuery.data?.getAllCountries || [],
    [locationQuery.data],
  );
  const selectedNewAgentCountry = React.useMemo(
    () =>
      countries.find((item: any) => String(item?.name || "") === String(newAgentCountry || "")) ||
      null,
    [countries, newAgentCountry],
  );
  const availableNewAgentStates = React.useMemo(
    () => selectedNewAgentCountry?.states || [],
    [selectedNewAgentCountry],
  );
  const selectedNewAgentState = React.useMemo(
    () =>
      availableNewAgentStates.find(
        (item: any) => String(item?.name || "") === String(newAgentState || ""),
      ) || null,
    [availableNewAgentStates, newAgentState],
  );
  const availableNewAgentCities = React.useMemo(
    () => selectedNewAgentState?.cities || [],
    [selectedNewAgentState],
  );
  const [unclaimCase, { loading: unclaimingCase }] = useMutation(
    UNCLAIM_CONCIERGE_CASE,
    {
      onError: (err) => setActionError(err.message || "Could not open case to claim pool."),
      onCompleted: () => {
        leadsQuery.refetch();
      },
    },
  );
  const [unclaimBroadcastGroup, { loading: unclaimingGroup }] = useMutation(
    UNCLAIM_CONCIERGE_BROADCAST_GROUP,
    {
      onError: (err) =>
        setActionError(err.message || "Could not open screening request to claim pool."),
      onCompleted: () => {
        screeningQuery.refetch();
      },
    },
  );
  const [createAgentUser, { loading: creatingAgent }] = useMutation(CREATE_AGENT_USER, {
    onError: (err) => setActionError(err.message || "Could not create agent."),
    onCompleted: () => {
      setActionInfo(
        "Agent access is ready. We emailed sign-in instructions automatically. New or temporary-password accounts were sent a temporary password with an expiry window. Existing users keep their password and can use Forgot Password if needed.",
      );
      agentUsersQuery.refetch({ includeInactive: true });
      closeAddAgentDialog();
    },
  });
  const [setAgentUserActive, { loading: togglingAgent }] = useMutation(
    SET_AGENT_USER_ACTIVE,
    {
      onError: (err) => setActionError(err.message || "Could not update agent."),
      onCompleted: () => {
        agentUsersQuery.refetch({ includeInactive: true });
        leadsQuery.refetch();
        screeningQuery.refetch();
      },
    },
  );
  const [setAgentUserPlatformAdmin, { loading: togglingAgentAdmin }] = useMutation(
    SET_AGENT_USER_PLATFORM_ADMIN,
    {
      onError: (err) => setActionError(err.message || "Could not update agent admin access."),
      onCompleted: () => {
        agentUsersQuery.refetch({ includeInactive: true });
      },
    },
  );
  const [reviewAgentApplication, { loading: reviewingApplication }] = useMutation(
    REVIEW_AGENT_APPLICATION,
    {
      onError: (err) =>
        setActionError(err.message || "Could not review application."),
      onCompleted: () => {
        setActionInfo("Application review saved.");
        agentApplicationsQuery.refetch({
          statuses: ["PENDING", "APPROVED", "REJECTED"],
          limit: 200,
          offset: 0,
        });
        agentUsersQuery.refetch({ includeInactive: true });
        setApplicationDialog(null);
        setApplicationReviewNotes("");
        setApplicationTempPassword("");
      },
    }
  );

  const leads = React.useMemo(
    () => leadsQuery.data?.getConciergeLeads ?? [],
    [leadsQuery.data],
  );
  const screeningGroups = React.useMemo(
    () => screeningQuery.data?.getConciergeBroadcastGroups ?? [],
    [screeningQuery.data],
  );
  const agentUsers = React.useMemo(
    () => agentUsersQuery.data?.agentUsers ?? [],
    [agentUsersQuery.data],
  );
  const agentApplications = React.useMemo(
    () => (agentApplicationsQuery.data?.agentApplications ?? []) as AgentApplicationRecord[],
    [agentApplicationsQuery.data],
  );
  const businesses = React.useMemo(
    () => businessesQuery.data?.businesses ?? [],
    [businessesQuery.data],
  );

  const agentState = React.useMemo(() => {
    const nextMap: Record<string, AgentRecord> = {};
    if (!canViewDashboard) return nextMap;

    agentUsers.forEach((user: any) => {
      if (!isVisibleAgentTabUser(user)) return;
      mergeAgent(nextMap, {
        userId: user?.id != null ? String(user.id) : null,
        email: user?.email,
        phone: user?.phone,
        address: user?.address,
        city: user?.city,
        state: user?.state,
        country: user?.country,
        zipcode: user?.zipcode,
        name: user?.username || user?.email || `Agent ${user?.id}`,
        source:
          String(user?.id || "") === String(providerUser?.userId || "")
            ? "current-user"
            : "discovered",
        privileges: {
          ...DEFAULT_PRIVILEGES,
          isAdmin: user?.isPlatformAdmin === true,
          isConcierge: isAssignableAgentUser(user),
        },
        isActive: Number(user?.status || 0) === 1,
      });
    });

    leads.forEach((lead: any) => {
      const agent = getAgentFromLead(lead);
      if (agent.name || agent.email || agent.userId) {
        mergeAgent(nextMap, {
          userId: agent.userId,
          email: agent.email,
          phone: agent.phone,
          name: agent.name,
          source: "discovered",
        });
      }
    });

    screeningGroups.forEach((group: any) => {
      const concierge = getScreeningConcierge(group);
      if (concierge.name || concierge.email || concierge.userId) {
        mergeAgent(nextMap, {
          userId: concierge.userId,
          email: concierge.email,
          phone: concierge.phone,
          name: concierge.name,
          source: "discovered",
        });
      }
    });

    return nextMap;
  }, [
    agentUsers,
    canViewDashboard,
    providerUser?.userId,
    leads,
    screeningGroups,
  ]);

  const allowedAgentIds = React.useMemo(() => {
    const ids = new Set<string>();
    agentUsers.forEach((user: any) => {
      if (!isVisibleAgentTabUser(user)) return;
      if (user?.id != null) ids.add(String(user.id));
    });
    return ids;
  }, [agentUsers]);

  const agentList = React.useMemo(
    () =>
      Object.values(agentState)
        .filter((agent) => {
          const userId = String(agent.userId || "").trim();
          return userId ? allowedAgentIds.has(userId) : false;
        })
        .sort((a, b) => {
          if (a.isActive !== b.isActive) {
            return a.isActive ? -1 : 1;
          }
          if (a.source !== b.source) {
            if (a.source === "current-user") return -1;
            if (b.source === "current-user") return 1;
          }
          return a.name.localeCompare(b.name);
        }),
    [agentState, allowedAgentIds],
  );
  const assignableAgents = React.useMemo(
    () =>
      agentList
        .filter(
          (agent) =>
            agent.isActive &&
            agent.privileges.isConcierge &&
            String(agent.userId || "").trim(),
        )
        .map((agent) => ({
          id: String(agent.userId),
          name: agent.name,
          email: agent.email || null,
        })),
    [agentList],
  );

  const leadCountsByBusiness = React.useMemo(() => {
    const map = new Map<string, number>();
    leads.forEach((lead: any) => {
      const key = String(lead?.businessId || lead?.business?.id || "");
      if (!key) return;
      map.set(key, (map.get(key) || 0) + 1);
    });
    return map;
  }, [leads]);

  const queueRows = React.useMemo(() => {
    const leadRows = leads.map((lead: any) => {
      const override = leadAssignmentOverrides[String(lead.id)] || null;
      const rawAgentBase = getAgentFromLead(lead);
      const rawAgent = override
        ? {
          ...rawAgentBase,
          userId: override.ownerAgentId ?? rawAgentBase.userId,
          name: override.ownerAgentName ?? rawAgentBase.name,
          email: override.email ?? rawAgentBase.email,
          phone: override.phone ?? rawAgentBase.phone,
        }
        : rawAgentBase;
      const agentDetails = buildAgentDetails(agentState, rawAgent);
      const resolvedAgentDetails =
        agentDetails && hasResolvedAgentIdentity(agentDetails) ? agentDetails : null;
      const conciergeBase = getConciergeStateFromLead(lead);
      const concierge = override
        ? {
          ...conciergeBase,
          ownerAgentId: override.ownerAgentId ?? conciergeBase?.ownerAgentId ?? null,
          ownerAgentName:
            override.ownerAgentName ?? conciergeBase?.ownerAgentName ?? null,
          status: override.status ?? conciergeBase?.status ?? null,
        }
        : conciergeBase;
      const history = getCaseHistory(concierge);
      const attribution = getAttributionMeta(lead);
      return {
        id: `lead-${lead.id}`,
        entityType: "lead",
        entityId: String(lead.id),
        type: "Case",
        subject:
          normalizeText(lead?.residentName) ||
          normalizeText(lead?.careLevelRequested) ||
          `Lead ${lead.id}`,
        status: normalizeText(lead?.status) || "NEW",
        businessName: normalizeText(lead?.business?.name) || "-",
        referrer:
          normalizeText(lead?.referrerContact?.name) ||
          normalizeText(lead?.referrer?.contactName) ||
          "-",
        agentName:
          (resolvedAgentDetails ? getAgentDisplayLabel(resolvedAgentDetails) : "") ||
          (concierge?.ownerAgentId ? "Assigned" : "Unassigned"),
        agentDetails: resolvedAgentDetails,
        ownerAgentId:
          normalizeText(concierge?.ownerAgentId) ||
          normalizeText(lead?.assignedAgentId) ||
          null,
        assignmentCount: history.assignments.length,
        activityCount: history.activity.length,
        history,
        leadSourceType: attribution.type,
        leadSourceLabel:
          attribution.type === "AGENT_SOURCED"
            ? `Agent${attribution.sourceAgentName ? ` - ${attribution.sourceAgentName}` : ""}`
            : "Platform",
        routingLabel: getRoutingLabel({
          ownerAgentId:
            normalizeText(concierge?.ownerAgentId) ||
            normalizeText(lead?.assignedAgentId) ||
            null,
          assignmentCount: history.assignments.length,
          intakeRoutingStatus: attribution.intakeRoutingStatus,
          routingMode: attribution.routingMode,
        }),
        createdAt: lead?.createdAt,
        href: "/care/agent",
      };
    });

    const screeningRows = screeningGroups.map((group: any) => {
      const seed = parseObject(group?.seedJson);
      const extra = parseObject(seed?.extra);
      const override = groupAssignmentOverrides[String(group.id)] || null;
      const rawConciergeBase = getScreeningConcierge(group);
      const rawConcierge = override
        ? {
          ...rawConciergeBase,
          userId: override.ownerAgentId ?? rawConciergeBase.userId,
          name: override.ownerAgentName ?? rawConciergeBase.name,
          email: override.email ?? rawConciergeBase.email,
          phone: override.phone ?? rawConciergeBase.phone,
          status: override.status ?? rawConciergeBase.status,
        }
        : rawConciergeBase;
      const agentDetails = buildAgentDetails(agentState, rawConcierge);
      const resolvedAgentDetails =
        agentDetails && hasResolvedAgentIdentity(agentDetails) ? agentDetails : null;
      const conciergeBase = getConciergeStateFromGroup(group);
      const concierge = override
        ? {
          ...conciergeBase,
          ownerAgentId: override.ownerAgentId ?? conciergeBase?.ownerAgentId ?? null,
          ownerAgentName:
            override.ownerAgentName ?? conciergeBase?.ownerAgentName ?? null,
          status: override.status ?? conciergeBase?.status ?? null,
        }
        : conciergeBase;
      const history = getCaseHistory(concierge);
      const attribution = getAttributionMeta(group);
      return {
        id: `group-${group.id}`,
        entityType: "group",
        entityId: String(group.id),
        type: "Screening",
        subject:
          normalizeText(seed?.residentKey) ||
          normalizeText(extra?.patientInitials) ||
          `Broadcast ${group.id}`,
        status: rawConcierge.status,
        businessName: `${(group?.invites || []).length} invited providers`,
        referrer: normalizeText(extra?.referrerContact?.name) || "-",
        agentName:
          (resolvedAgentDetails ? getAgentDisplayLabel(resolvedAgentDetails) : "") ||
          (concierge?.ownerAgentId ? "Assigned" : "Unassigned"),
        agentDetails: resolvedAgentDetails,
        ownerAgentId:
          normalizeText(concierge?.ownerAgentId) ||
          normalizeText(group?.assignedAgentId) ||
          null,
        assignmentCount: history.assignments.length,
        activityCount: history.activity.length,
        history,
        leadSourceType: attribution.type,
        leadSourceLabel:
          attribution.type === "AGENT_SOURCED"
            ? `Agent${attribution.sourceAgentName ? ` - ${attribution.sourceAgentName}` : ""}`
            : "Platform",
        routingLabel: getRoutingLabel({
          ownerAgentId:
            normalizeText(concierge?.ownerAgentId) ||
            normalizeText(group?.assignedAgentId) ||
            null,
          assignmentCount: history.assignments.length,
          intakeRoutingStatus: attribution.intakeRoutingStatus,
          routingMode: attribution.routingMode,
        }),
        createdAt: group?.createdAt,
        href: `/care/referrals/group/${group.id}`,
      };
    });

    return [...leadRows, ...screeningRows].sort((a, b) => {
      const ta = Date.parse(String(a.createdAt || "")) || 0;
      const tb = Date.parse(String(b.createdAt || "")) || 0;
      return tb - ta;
    });
  }, [agentState, leadAssignmentOverrides, leads, groupAssignmentOverrides, screeningGroups]);

  const queueAgentOptions = React.useMemo(() => {
    const options = new Map<string, { id: string; label: string }>();

    queueRows.forEach((row) => {
      const ownerAgentId = normalizeText(row.ownerAgentId);
      if (!ownerAgentId) return;

      const knownAgent =
        row.agentDetails ||
        findAgentRecord(agentState, {
          userId: ownerAgentId,
          name: row.agentName,
        });
      const label =
        (knownAgent ? getAgentDisplayLabel(knownAgent) : "") ||
        normalizeText(row.agentName) ||
        `Agent ${ownerAgentId}`;

      options.set(ownerAgentId, {
        id: ownerAgentId,
        label,
      });
    });

    return Array.from(options.values()).sort((a, b) => a.label.localeCompare(b.label));
  }, [agentState, queueRows]);

  const filteredQueueRows = React.useMemo(() => {
    let rows = queueRows;
    if (queueAgentFilter === "unassigned") {
      rows = rows.filter((row) => !String(row.ownerAgentId || "").trim());
    } else if (queueAgentFilter !== "all") {
      rows = rows.filter(
        (row) => String(row.ownerAgentId || "") === String(queueAgentFilter),
      );
    }
    if (queueSourceFilter === "platform") {
      rows = rows.filter((row) => String(row.leadSourceType || "") === "PLATFORM_DIRECT");
    } else if (queueSourceFilter === "agent") {
      rows = rows.filter((row) => String(row.leadSourceType || "") === "AGENT_SOURCED");
    }
    return rows;
  }, [queueAgentFilter, queueRows, queueSourceFilter]);

  const subscriberBusinesses = React.useMemo(
    () =>
      [...businesses]
        .filter((business: any) => {
          const hasPlan =
            Array.isArray(business?.plans) && business.plans.length > 0;
          const hasClaimSignal =
            normalizeText(business?.claimStatus) ||
            leadCountsByBusiness.get(String(business?.id));
          return hasPlan || hasClaimSignal;
        })
        .sort((a: any, b: any) => a.name.localeCompare(b.name)),
    [businesses, leadCountsByBusiness],
  );

  const billingLeads = React.useMemo(
    () =>
      [...leads]
        .filter((lead: any) =>
          ["PLACED", "PENDING_DECISION", "TOUR_SCHEDULED", "CLOSED"].includes(
            String(lead?.status || "").toUpperCase(),
          ),
        )
        .sort((a: any, b: any) => {
          const ta = Date.parse(String(a?.createdAt || "")) || 0;
          const tb = Date.parse(String(b?.createdAt || "")) || 0;
          return tb - ta;
        }),
    [leads],
  );
  const pendingApplicationCount = React.useMemo(
    () =>
      agentApplications.filter(
        (application) => String(application.status || "").toUpperCase() === "PENDING",
      ).length,
    [agentApplications],
  );
  const agentFilterOptions = React.useMemo(() => {
    const uniqueSorted = (values: string[]) =>
      Array.from(new Set(values.filter(Boolean))).sort((a, b) => a.localeCompare(b));
    const normalizedStateFilter = normalizeText(agentFilterState).toLowerCase();
    const stateScopedAgents = normalizedStateFilter
      ? agentList.filter(
        (agent) => normalizeText(agent.state).toLowerCase() === normalizedStateFilter,
      )
      : agentList;

    return {
      states: uniqueSorted(agentList.map((agent) => normalizeText(agent.state))),
      cities: uniqueSorted(stateScopedAgents.map((agent) => normalizeText(agent.city))),
      zipcodes: uniqueSorted(stateScopedAgents.map((agent) => normalizeText(agent.zipcode))),
    };
  }, [agentFilterState, agentList]);
  const filteredAgentList = React.useMemo(() => {
    const cityFilter = normalizeText(agentFilterCity).toLowerCase();
    const stateFilter = normalizeText(agentFilterState).toLowerCase();
    const zipFilter = normalizeText(agentFilterZip).toLowerCase();

    return agentList.filter((agent) => {
      const city = normalizeText(agent.city).toLowerCase();
      const state = normalizeText(agent.state).toLowerCase();
      const zipcode = normalizeText(agent.zipcode).toLowerCase();

      if (cityFilter && !city.includes(cityFilter)) return false;
      if (stateFilter && !state.includes(stateFilter)) return false;
      if (zipFilter && !zipcode.includes(zipFilter)) return false;
      return true;
    });
  }, [agentFilterCity, agentFilterState, agentFilterZip, agentList]);

  const claimedBusinessCount = React.useMemo(
    () =>
      subscriberBusinesses.filter(
        (business: any) =>
          normalizeText(business?.claimStatus).toLowerCase() === "claimed",
      ).length,
    [subscriberBusinesses],
  );

  if (!canViewDashboard) {
    return (
      <Alert severity="error">
        Platform admin access is required to open the admin dashboard.
      </Alert>
    );
  }

  if (
    leadsQuery.loading ||
    screeningQuery.loading ||
    agentUsersQuery.loading ||
    (shouldLoadApplications && agentApplicationsQuery.loading) ||
    (shouldLoadSubscribers && businessesQuery.loading)
  ) {
    return <LinearProgress sx={{ my: 2 }} />;
  }

  if (
    leadsQuery.error ||
    screeningQuery.error ||
    agentUsersQuery.error ||
    (shouldLoadApplications && agentApplicationsQuery.error) ||
    (shouldLoadSubscribers && businessesQuery.error)
  ) {
    return (
      <Alert severity="error">
        {String(
          leadsQuery.error?.message ||
          screeningQuery.error?.message ||
          agentUsersQuery.error?.message ||
          (shouldLoadApplications ? agentApplicationsQuery.error?.message : "") ||
          (shouldLoadSubscribers ? businessesQuery.error?.message : "") ||
          "Could not load admin dashboard data.",
        )}
      </Alert>
    );
  }

  const openAssignmentDialog = (row: any) => {
    setAssignmentDialog(row);
    setAssignmentTargetId("");
    setAssignmentNote("");
  };

  const closeAssignmentDialog = () => {
    setAssignmentDialog(null);
    setAssignmentTargetId("");
    setAssignmentNote("");
  };

  const submitAssignment = async () => {
    if (!assignmentDialog || !assignmentTargetId) return;
    setActionError(null);
    let resp: any = null;
    if (assignmentDialog.entityType === "lead") {
      if (
        assignmentDialog.ownerAgentId &&
        String(assignmentDialog.ownerAgentId) === String(providerUser?.userId || "")
      ) {
        resp = await transferCase({
          variables: {
            leadId: assignmentDialog.entityId,
            targetAgentId: assignmentTargetId,
            note: assignmentNote || null,
          },
        });
      } else {
        resp = await reassignCase({
          variables: {
            leadId: assignmentDialog.entityId,
            targetAgentId: assignmentTargetId,
            note: assignmentNote || null,
          },
        });
      }
    } else {
      if (
        assignmentDialog.ownerAgentId &&
        String(assignmentDialog.ownerAgentId) === String(providerUser?.userId || "")
      ) {
        resp = await transferBroadcastGroup({
          variables: {
            groupId: assignmentDialog.entityId,
            targetAgentId: assignmentTargetId,
            note: assignmentNote || null,
          },
        });
      } else {
        resp = await reassignBroadcastGroup({
          variables: {
            groupId: assignmentDialog.entityId,
            targetAgentId: assignmentTargetId,
            note: assignmentNote || null,
          },
        });
      }
    }
    const success =
      resp?.data?.transferConciergeCase?.success ??
      resp?.data?.reassignConciergeCase?.success ??
      resp?.data?.transferConciergeBroadcastGroup?.success ??
      resp?.data?.reassignConciergeBroadcastGroup?.success;
    if (success === false) return;

    const selectedAgent =
      assignableAgents.find(
        (agent) => String(agent.id || "") === String(assignmentTargetId || ""),
      ) || null;
    const nextConcierge =
      resp?.data?.transferConciergeCase?.concierge ??
      resp?.data?.reassignConciergeCase?.concierge ??
      resp?.data?.transferConciergeBroadcastGroup?.concierge ??
      resp?.data?.reassignConciergeBroadcastGroup?.concierge ??
      null;
    const nextOverride: AssignmentOverride = {
      ownerAgentId:
        String(nextConcierge?.ownerAgentId ?? assignmentTargetId ?? "").trim() || null,
      ownerAgentName:
        normalizeText(nextConcierge?.ownerAgentName) ||
        normalizeText(selectedAgent?.name) ||
        null,
      status: normalizeText(nextConcierge?.status) || null,
      email: normalizeText(selectedAgent?.email) || null,
      phone: null,
    };

    if (assignmentDialog.entityType === "lead") {
      setLeadAssignmentOverrides((prev) => ({
        ...prev,
        [String(assignmentDialog.entityId)]: nextOverride,
      }));
    } else {
      setGroupAssignmentOverrides((prev) => ({
        ...prev,
        [String(assignmentDialog.entityId)]: nextOverride,
      }));
    }
    closeAssignmentDialog();
  };

  const openToClaimPool = async (row: any) => {
    if (!row?.entityId) return;
    setActionError(null);
    if (row.entityType === "lead") {
      const resp = await unclaimCase({
        variables: { leadId: row.entityId },
      });
      const success = resp?.data?.unclaimConciergeCase?.success;
      if (success === false) return;
      setLeadAssignmentOverrides((prev) => ({
        ...prev,
        [String(row.entityId)]: {
          ownerAgentId: null,
          ownerAgentName: null,
          status: normalizeText(resp?.data?.unclaimConciergeCase?.concierge?.status) || "OPEN",
          email: null,
          phone: null,
        },
      }));
      return;
    }
    const resp = await unclaimBroadcastGroup({
      variables: { groupId: row.entityId },
    });
    const success = resp?.data?.unclaimConciergeBroadcastGroup?.success;
    if (success === false) return;
    setGroupAssignmentOverrides((prev) => ({
      ...prev,
      [String(row.entityId)]: {
        ownerAgentId: null,
        ownerAgentName: null,
        status:
          normalizeText(resp?.data?.unclaimConciergeBroadcastGroup?.concierge?.status) ||
          "REQUESTED",
        email: null,
        phone: null,
      },
    }));
  };

  const submitNewAgent = async () => {
    if (!newAgentName.trim() || !newAgentEmail.trim()) {
      setActionError("Name and email are required.");
      return;
    }
    setActionError(null);
    await createAgentUser({
      variables: {
        username: newAgentName.trim(),
        email: newAgentEmail.trim(),
        phone: newAgentPhone.trim() || null,
        address: newAgentAddress.trim() || null,
        city: newAgentCity.trim() || null,
        state: newAgentState.trim() || null,
        country: newAgentCountry.trim() || null,
        zipcode: newAgentZipcode.trim() || null,
        password: newAgentPassword.trim() || null,
        isPlatformAdmin: newAgentAdmin,
      },
    });
  };

  const toggleAgentActive = async (agent: AgentRecord) => {
    if (!agent.userId) return;
    setActionError(null);
    await setAgentUserActive({
      variables: {
        userId: agent.userId,
        isActive: !agent.isActive,
      },
    });
  };

  const toggleAgentAdmin = async (agent: AgentRecord) => {
    if (!agent.userId) return;
    setActionError(null);
    await setAgentUserPlatformAdmin({
      variables: {
        userId: agent.userId,
        isPlatformAdmin: !agent.privileges.isAdmin,
      },
    });
  };

  return (
    <Box
      sx={{ maxWidth: 1380, mx: "auto", px: { xs: 1, sm: 2 }, py: { xs: 2, sm: 3 } }}
    >
      {actionError ? (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setActionError(null)}>
          {actionError}
        </Alert>
      ) : null}
      {actionInfo ? (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setActionInfo(null)}>
          {actionInfo}
        </Alert>
      ) : null}
      <Paper
        variant="outlined"
        sx={{
          p: { xs: 2, sm: 3 },
          borderRadius: 4,
          background: (theme) =>
            `linear-gradient(135deg, ${alpha(theme.palette.info.main, 0.1)}, ${alpha(
              theme.palette.background.paper,
              0.97,
            )})`,
        }}
      >
        <Stack
          direction={{ xs: "column", lg: "row" }}
          justifyContent="space-between"
          spacing={2}
        >
          <Box>
            <Typography variant="overline" color="primary.main">
              Agent Operations
            </Typography>
            <Typography variant="h4" fontWeight={800}>
              Agent Admin Dashboard
            </Typography>
            <Typography
              variant="body1"
              color="text.secondary"
              sx={{ maxWidth: 760, mt: 1 }}
            >
              Manage agents, review subscribers and claimed businesses, monitor
              case ownership, and watch billing follow-up from one place.
            </Typography>
          </Box>
          <Stack
            direction={{ xs: "column", sm: "row" }}
            spacing={1}
            alignSelf="flex-start"
          >
            {canManageAgents ? (
              <Button variant="contained" onClick={() => setAddAgentOpen(true)}>
                Add agent
              </Button>
            ) : null}
            <Button
              variant="outlined"
              component={RouterLink}
              to="/care/agent"
              endIcon={<OpenInNewIcon />}
            >
              Open agent workspace
            </Button>
            <Button
              variant="contained"
              component={RouterLink}
              to="/care/agreement"
              endIcon={<OpenInNewIcon />}
            >
              Provider agreements
            </Button>
          </Stack>
        </Stack>

        <Alert severity="info" sx={{ mt: 2 }}>
          Real agents are assignable. Admin-only users stay visible here for management,
          and inactive users stay visible for audit, but neither should receive new
          assignments unless they are actual agent accounts.
        </Alert>
        <Alert
          severity="info"
          sx={{ mt: 2 }}
          action={
            <Button
              color="inherit"
              size="small"
              onClick={() => {
                navigator.clipboard
                  ?.writeText(`${window.location.origin}/care/agent/apply`)
                  .then(() => setActionInfo("Agent application link copied."));
              }}
            >
              Copy link
            </Button>
          }
        >
          Agent application page (Apply to become a Care Agent): {window.location.origin}/care/agent/apply
        </Alert>

        <Box
          sx={{
            mt: 3,
            display: "grid",
            gridTemplateColumns: {
              xs: "1fr",
              sm: "repeat(2, minmax(0, 1fr))",
              lg: "repeat(4, minmax(0, 1fr))",
            },
            gap: 2,
          }}
        >
          <MetricCard
            title="Open queue"
            value={filteredQueueRows.length}
            subtitle={`${leads.length} cases and ${screeningGroups.length} screening requests`}
            icon={<SupportAgentOutlinedIcon fontSize="large" />}
          />
          <MetricCard
            title="Mapped agents"
            value={agentList.length}
            subtitle="Resolved from user-linked records in current data"
            icon={<GroupsOutlinedIcon fontSize="large" />}
          />
          <MetricCard
            title="Agent applications"
            value={pendingApplicationCount}
            subtitle={`${agentApplications.length} total submitted applications`}
            icon={<GroupsOutlinedIcon fontSize="large" />}
          />
          <MetricCard
            title="Subscriber businesses"
            value={subscriberBusinesses.length}
            subtitle={`${claimedBusinessCount} marked claimed in current business data`}
            icon={<BusinessOutlinedIcon fontSize="large" />}
          />
          <MetricCard
            title="Billing watch"
            value={billingLeads.length}
            subtitle="Placed or decision-stage leads requiring billing visibility"
            icon={<SavingsOutlinedIcon fontSize="large" />}
          />
        </Box>

        <Card
          variant="outlined"
          sx={{
            mt: 2,
            borderRadius: 3,
            background: (theme) =>
              `linear-gradient(180deg, ${alpha(theme.palette.info.main, 0.05)}, ${alpha(
                theme.palette.background.paper,
                0.98,
              )})`,
          }}
        >
          <CardContent>
            <Stack spacing={1.5}>
              <Box>
                <Typography variant="overline" color="text.secondary">
                  Quick Landing Checks
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Open the public landing pages quickly to validate routing and content.
                </Typography>
              </Box>
              <Stack direction={{ xs: "column", sm: "row" }} spacing={1.25} useFlexGap flexWrap="wrap">
                <Button
                  component={RouterLink}
                  to="/"
                  variant="outlined"
                  endIcon={<OpenInNewIcon />}
                >
                  Root
                </Button>
                <Button
                  component={RouterLink}
                  to="/care"
                  variant="outlined"
                  endIcon={<OpenInNewIcon />}
                >
                  Care Home
                </Button>
                <Button
                  component={RouterLink}
                  to="/care"
                  variant="outlined"
                  endIcon={<OpenInNewIcon />}
                >
                  Care Landing
                </Button>
                <Button
                  component={RouterLink}
                  to="/booking"
                  variant="outlined"
                  endIcon={<OpenInNewIcon />}
                >
                  Booking Landing
                </Button>
              </Stack>
            </Stack>
          </CardContent>
        </Card>
      </Paper>
      <Paper variant="outlined" sx={{ mt: 2, borderRadius: 3, overflow: "hidden" }}>
        <Tabs
          value={activeTab}
          onChange={(_event, next) => setActiveTab(next)}
          variant="scrollable"
          scrollButtons="auto"
          allowScrollButtonsMobile
          sx={{
            px: { xs: 0.5, sm: 1.5 },
            "& .MuiTab-root": {
              minHeight: { xs: 50, sm: 56 },
              minWidth: { xs: "auto", sm: 96 },
              px: { xs: 1.5, sm: 2 },
              textTransform: "none",
              fontWeight: 600,
              fontSize: { xs: "0.85rem", sm: "0.95rem" },
              whiteSpace: "nowrap",
            },
            "& .MuiTabs-scrollButtons.Mui-disabled": {
              opacity: 0.3,
            },
          }}
        >
          <Tab label="Queue" />
          <Tab label="Agents" />
          <Tab label={`Applications${pendingApplicationCount ? ` (${pendingApplicationCount})` : ""}`} />
          <Tab label="Subscribers" />
          <Tab label="Billing" />
        </Tabs>
        <Divider />

        {activeTab === 0 ? (
          <Box sx={{ p: 2 }}>
            <Stack
              direction={{ xs: "column", sm: "row" }}
              spacing={2}
              useFlexGap
              sx={{ mb: 2, flexWrap: "wrap" }}
              alignItems={{ xs: "stretch", sm: "center" }}
            >
              <TextField
                select
                size="small"
                label="Assigned agent"
                value={queueAgentFilter}
                onChange={(event) => setQueueAgentFilter(event.target.value)}
                sx={{ minWidth: { xs: "100%", sm: 280 } }}
              >
                <MenuItem value="all">All agents</MenuItem>
                <MenuItem value="unassigned">Unassigned</MenuItem>
                {queueAgentOptions.map((agent) => (
                  <MenuItem key={agent.id} value={agent.id}>
                    {agent.label}
                  </MenuItem>
                ))}
              </TextField>
              <TextField
                select
                size="small"
                label="Lead source"
                value={queueSourceFilter}
                onChange={(event) => setQueueSourceFilter(event.target.value)}
                sx={{ minWidth: { xs: "100%", sm: 200 } }}
              >
                <MenuItem value="all">All sources</MenuItem>
                <MenuItem value="platform">Platform direct</MenuItem>
                <MenuItem value="agent">Agent sourced</MenuItem>
              </TextField>
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{ width: { xs: "100%", sm: "auto" } }}
              >
                {filteredQueueRows.length} of {queueRows.length} queue items
              </Typography>
            </Stack>
            <ResponsiveTableArea minWidth={1180}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Type</TableCell>
                    <TableCell>Subject</TableCell>
                    <TableCell>Lead source</TableCell>
                    <TableCell>Routing</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell>Assigned agent</TableCell>
                    <TableCell>Referrer</TableCell>
                    <TableCell>Business</TableCell>
                    <TableCell>Created</TableCell>
                    <TableCell align="right">Action</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {filteredQueueRows.map((row) => (
                    <TableRow key={row.id} hover>
                      <TableCell>{row.type}</TableCell>
                      <TableCell>{row.subject}</TableCell>
                      <TableCell>
                        <StatusChip
                          label={row.leadSourceLabel}
                          color={
                            row.leadSourceType === "AGENT_SOURCED" ? "info" : "default"
                          }
                        />
                      </TableCell>
                      <TableCell>
                        <StatusChip
                          label={row.routingLabel}
                          color={row.ownerAgentId ? "success" : "warning"}
                        />
                      </TableCell>
                      <TableCell>
                        <StatusChip
                          label={row.status}
                          color={
                            ["PLACED", "CLOSED"].includes(row.status)
                              ? "success"
                              : row.status === "REQUESTED"
                                ? "warning"
                                : "info"
                          }
                        />
                      </TableCell>
                      <TableCell>
                        {row.agentDetails ? (
                          <Stack
                            direction="row"
                            spacing={1}
                            alignItems="center"
                            useFlexGap
                            flexWrap="wrap"
                          >
                            <Typography variant="body2">{row.agentName}</Typography>
                            <Button
                              size="small"
                              variant="text"
                              onClick={() => setSelectedAgent(row.agentDetails)}
                            >
                              View
                            </Button>
                          </Stack>
                        ) : (
                          row.agentName
                        )}
                      </TableCell>
                      <TableCell>{row.referrer}</TableCell>
                      <TableCell>{row.businessName}</TableCell>
                      <TableCell>{formatDate(row.createdAt)}</TableCell>
                      <TableCell align="right">
                        <Stack
                          direction="row"
                          spacing={1}
                          justifyContent="flex-end"
                          useFlexGap
                          flexWrap="wrap"
                        >
                          <Button
                            size="small"
                            variant="outlined"
                            onClick={() => openAssignmentDialog(row)}
                          >
                            {row.ownerAgentId ? "Reassign" : "Assign"}
                          </Button>
                          <Button
                            size="small"
                            variant="text"
                            onClick={() => openToClaimPool(row)}
                            disabled={
                              !row.ownerAgentId ||
                              unclaimingCase ||
                              unclaimingGroup
                            }
                          >
                            Open claim pool
                          </Button>
                          <Button
                            size="small"
                            variant="text"
                            onClick={() => setHistoryDialog(row)}
                          >
                            History
                          </Button>
                          <Button
                            size="small"
                            variant="outlined"
                            component={RouterLink}
                            to={row.href}
                          >
                            Open
                          </Button>
                        </Stack>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </ResponsiveTableArea>
          </Box>
        ) : null}

        {activeTab === 1 ? (
          <Box sx={{ p: 2 }}>
            <Stack
              direction={{ xs: "column", lg: "row" }}
              spacing={2}
              sx={{ mb: 2 }}
              alignItems={{ xs: "stretch", lg: "flex-start" }}
            >
              <TextField
                select
                size="small"
                label="Filter by state"
                value={agentFilterState}
                onChange={(event) => {
                  setAgentFilterState(event.target.value);
                  setAgentFilterCity("");
                  setAgentFilterZip("");
                }}
                helperText=" "
                sx={{ minWidth: { xs: "100%", lg: 180 }, flex: { lg: "0 0 180px" } }}
              >
                <MenuItem value="">All states</MenuItem>
                {agentFilterOptions.states.map((state) => (
                  <MenuItem key={state} value={state}>
                    {state}
                  </MenuItem>
                ))}
              </TextField>
              <TextField
                select
                size="small"
                label="Filter by city"
                value={agentFilterCity}
                onChange={(event) => setAgentFilterCity(event.target.value)}
                disabled={!agentFilterState}
                helperText={!agentFilterState ? "Select state first" : " "}
                sx={{ minWidth: { xs: "100%", lg: 220 }, flex: { lg: "0 0 220px" } }}
              >
                <MenuItem value="">All cities</MenuItem>
                {agentFilterOptions.cities.map((city) => (
                  <MenuItem key={city} value={city}>
                    {city}
                  </MenuItem>
                ))}
              </TextField>
              <TextField
                select
                size="small"
                label="Filter by zip"
                value={agentFilterZip}
                onChange={(event) => setAgentFilterZip(event.target.value)}
                disabled={!agentFilterState}
                helperText={!agentFilterState ? "Select state first" : " "}
                sx={{ minWidth: { xs: "100%", lg: 160 }, flex: { lg: "0 0 160px" } }}
              >
                <MenuItem value="">All zips</MenuItem>
                {agentFilterOptions.zipcodes.map((zipcode) => (
                  <MenuItem key={zipcode} value={zipcode}>
                    {zipcode}
                  </MenuItem>
                ))}
              </TextField>
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{ ml: { lg: "auto" }, whiteSpace: "nowrap" }}
              >
                {filteredAgentList.length} of {agentList.length} agents
              </Typography>
            </Stack>
            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: {
                  xs: "1fr",
                  xl: "repeat(2, minmax(0, 1fr))",
                },
                gap: 2,
              }}
            >
              {filteredAgentList.map((agent) => (
                <Paper key={agent.id} variant="outlined" sx={{ p: 2, borderRadius: 3 }}>
                  <Stack spacing={1.5}>
                    {String(agent.userId || "") === String(providerUser?.userId || "") ? (
                      <Alert severity="info" sx={{ py: 0 }}>
                        This user is your current admin account. Admin and inactive controls are
                        disabled here to prevent accidental lockout.
                      </Alert>
                    ) : null}
                    <Stack direction="row" justifyContent="space-between" spacing={2}>
                      <Box>
                        <Typography variant="h6">{agent.name}</Typography>
                        <Typography variant="body2" color="text.secondary">
                          {agent.email || "No email on record"}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          {agent.phone || "No phone on record"}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          {getAgentLocationLabel(agent)}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {agent.userId ? `User ${agent.userId}` : "No linked user id"}
                        </Typography>
                      </Box>
                      <Stack
                        direction="row"
                        spacing={1}
                        useFlexGap
                        flexWrap="wrap"
                        justifyContent="flex-end"
                      >
                        <StatusChip
                          label={agent.isActive ? "Active" : "Inactive"}
                          color={agent.isActive ? "success" : "default"}
                        />
                        {agent.privileges.isConcierge ? (
                          <StatusChip label="Agent" color="info" />
                        ) : null}
                        {agent.privileges.isAdmin ? (
                          <StatusChip label="Admin" color="warning" />
                        ) : null}
                        <StatusChip
                          label={
                            isAgentProfileComplete(agent)
                              ? "Routing profile ready"
                              : "Needs routing profile"
                          }
                          color={isAgentProfileComplete(agent) ? "success" : "warning"}
                        />
                        {String(agent.userId || "") === String(providerUser?.userId || "") ? (
                          <StatusChip label="Current user" color="info" />
                        ) : null}
                        <StatusChip label="Mapped user" color="success" />
                      </Stack>
                    </Stack>
                    {canManageAgents && agent.userId ? (
                      <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap">
                        {agent.phone ? (
                          <Button
                            size="small"
                            variant="outlined"
                            component="a"
                            href={`tel:${agent.phone}`}
                          >
                            Call
                          </Button>
                        ) : null}
                        {agent.email ? (
                          <Button
                            size="small"
                            variant="outlined"
                            component="a"
                            href={`mailto:${agent.email}`}
                          >
                            Email
                          </Button>
                        ) : null}
                        <Button
                          size="small"
                          variant={agent.privileges.isAdmin ? "outlined" : "contained"}
                          color={agent.privileges.isAdmin ? "warning" : "info"}
                          disabled={
                            togglingAgentAdmin ||
                            String(agent.userId) === String(providerUser?.userId || "")
                          }
                          onClick={() => toggleAgentAdmin(agent)}
                        >
                          {agent.privileges.isAdmin ? "Remove admin" : "Make admin"}
                        </Button>
                        <Button
                          size="small"
                          variant={agent.isActive ? "outlined" : "contained"}
                          color={agent.isActive ? "warning" : "success"}
                          disabled={
                            togglingAgent ||
                            String(agent.userId) === String(providerUser?.userId || "")
                          }
                          onClick={() => setAgentStatusConfirm(agent)}
                        >
                          {agent.isActive ? "Make inactive" : "Make active"}
                        </Button>
                      </Stack>
                    ) : null}
                  </Stack>
                </Paper>
              ))}
            </Box>
          </Box>
        ) : null}
        {activeTab === 2 ? (
          <Box sx={{ p: 2 }}>
            <Stack
              direction={{ xs: "column", sm: "row" }}
              justifyContent="space-between"
              spacing={2}
              sx={{ mb: 2 }}
            >
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{ overflowWrap: "anywhere" }}
              >
                Public apply link (Apply to become a Care Agent): {window.location.origin}/care/agent/apply
              </Typography>
              <Button
                size="small"
                variant="outlined"
                onClick={() => {
                  navigator.clipboard
                    ?.writeText(`${window.location.origin}/care/agent/apply`)
                    .then(() => setActionInfo("Agent application link copied."));
                }}
              >
                Copy apply link
              </Button>
            </Stack>
            <ResponsiveTableArea minWidth={980}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Name</TableCell>
                    <TableCell>Email</TableCell>
                    <TableCell>Location</TableCell>
                    <TableCell>Service areas</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell>Submitted</TableCell>
                    <TableCell align="right">Action</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {agentApplications.map((application) => (
                    <TableRow key={application.id} hover>
                      <TableCell>
                        <Stack spacing={0.5}>
                          <Typography variant="body2" fontWeight={600}>
                            {application.username || "-"}
                          </Typography>
                          {application.specialties ? (
                            <Typography variant="caption" color="text.secondary">
                              {application.specialties}
                            </Typography>
                          ) : null}
                        </Stack>
                      </TableCell>
                      <TableCell>
                        <Stack spacing={0.5}>
                          <Typography variant="body2">{application.email || "-"}</Typography>
                          {application.phone ? (
                            <Typography variant="caption" color="text.secondary">
                              {application.phone}
                            </Typography>
                          ) : null}
                        </Stack>
                      </TableCell>
                      <TableCell>
                        {[
                          normalizeText(application.city),
                          normalizeText(application.state),
                          normalizeText(application.country),
                          normalizeText(application.zipcode),
                        ]
                          .filter(Boolean)
                          .join(", ") || "-"}
                      </TableCell>
                      <TableCell>{application.serviceAreas || "-"}</TableCell>
                      <TableCell>
                        <StatusChip
                          label={application.status || "PENDING"}
                          color={
                            String(application.status || "").toUpperCase() === "APPROVED"
                              ? "success"
                              : String(application.status || "").toUpperCase() === "REJECTED"
                                ? "default"
                                : "warning"
                          }
                        />
                      </TableCell>
                      <TableCell>{formatDate(application.createdAt)}</TableCell>
                      <TableCell align="right">
                        <Stack
                          direction="row"
                          spacing={1}
                          justifyContent="flex-end"
                          useFlexGap
                          flexWrap="wrap"
                        >
                          <Button
                            size="small"
                            variant="contained"
                            disabled={reviewingApplication}
                            onClick={() => {
                              setApplicationDialog({
                                application,
                                decision: "APPROVED",
                              });
                              setApplicationReviewNotes(application.reviewNotes || "");
                              setApplicationTempPassword("");
                            }}
                          >
                            Approve
                          </Button>
                          <Button
                            size="small"
                            variant="outlined"
                            color="warning"
                            disabled={reviewingApplication}
                            onClick={() => {
                              setApplicationDialog({
                                application,
                                decision: "REJECTED",
                              });
                              setApplicationReviewNotes(application.reviewNotes || "");
                              setApplicationTempPassword("");
                            }}
                          >
                            Reject
                          </Button>
                        </Stack>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </ResponsiveTableArea>
          </Box>
        ) : null}

        {activeTab === 3 ? (
          <Box sx={{ p: 2 }}>
            <ResponsiveTableArea minWidth={1040}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Business</TableCell>
                    <TableCell>City</TableCell>
                    <TableCell>Claim</TableCell>
                    <TableCell>Claim status</TableCell>
                    <TableCell>Subscription</TableCell>
                    <TableCell>Plans</TableCell>
                    <TableCell>Staff</TableCell>
                    <TableCell>Referral volume</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {subscriberBusinesses.map((business: any) => (
                    <TableRow key={business.id} hover>
                      <TableCell>
                        <Stack spacing={0.5}>
                          <Typography variant="body2" fontWeight={600}>
                            {business.name}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            /b/{business.url || business.id}
                          </Typography>
                        </Stack>
                      </TableCell>
                      <TableCell>{business.cityName || "-"}</TableCell>
                      <TableCell>
                        <BusinessClaimStatusCell businessId={business.id} />
                      </TableCell>
                      <TableCell>
                        <StatusChip
                          label={normalizeText(business.claimStatus) || "Unknown"}
                          color={
                            normalizeText(business.claimStatus).toLowerCase() === "claimed"
                              ? "success"
                              : "default"
                          }
                        />
                      </TableCell>
                      <TableCell>
                        <BusinessSubscriptionCell businessId={business.id} />
                      </TableCell>
                      <TableCell>
                        {Array.isArray(business.plans) && business.plans.length
                          ? business.plans.map((plan: any) => plan.name).join(", ")
                          : "None"}
                      </TableCell>
                      <TableCell>{(business.staffs || []).length}</TableCell>
                      <TableCell>{leadCountsByBusiness.get(String(business.id)) || 0}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </ResponsiveTableArea>
          </Box>
        ) : null}

        {activeTab === 4 ? (
          <Box sx={{ p: 2 }}>
            <ResponsiveTableArea minWidth={760}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Lead</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell>Business</TableCell>
                    <TableCell>Referrer</TableCell>
                    <TableCell>Created</TableCell>
                    <TableCell>Billing status</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {billingLeads.map((lead: any) => (
                    <TableRow key={lead.id} hover>
                      <TableCell>
                        <Stack spacing={0.25}>
                          <Typography variant="body2" fontWeight={600}>
                            {normalizeText(lead?.residentName) ||
                              normalizeText(lead?.careLevelRequested) ||
                              `Lead ${lead.id}`}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            ID {lead.id}
                          </Typography>
                        </Stack>
                      </TableCell>
                      <TableCell>
                        <StatusChip
                          label={lead.status || "Unknown"}
                          color={
                            ["PLACED", "CLOSED"].includes(
                              String(lead.status || "").toUpperCase(),
                            )
                              ? "success"
                              : "warning"
                          }
                        />
                      </TableCell>
                      <TableCell>{lead?.business?.name || "-"}</TableCell>
                      <TableCell>
                        {lead?.referrerContact?.name ||
                          lead?.referrer?.contactName ||
                          "-"}
                      </TableCell>
                      <TableCell>{formatDate(lead?.createdAt)}</TableCell>
                      <TableCell>
                        <LeadBillingCell lead={lead} />
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </ResponsiveTableArea>
          </Box>
        ) : null}
      </Paper>
      <Dialog
        open={Boolean(agentStatusConfirm)}
        onClose={() => setAgentStatusConfirm(null)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>
          {agentStatusConfirm?.isActive ? "Make Agent Inactive" : "Make Agent Active"}
        </DialogTitle>
        <DialogContent dividers>
          <Typography variant="body2">
            {agentStatusConfirm?.isActive
              ? `Make ${agentStatusConfirm?.name || "this agent"} inactive? They will no longer be able to log in or receive assignments.`
              : `Make ${agentStatusConfirm?.name || "this agent"} active again? They will be available in assignments and can access the agent workspace.`}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAgentStatusConfirm(null)}>Cancel</Button>
          <Button
            variant="contained"
            color={agentStatusConfirm?.isActive ? "warning" : "success"}
            disabled={togglingAgent}
            onClick={async () => {
              if (!agentStatusConfirm) return;
              await toggleAgentActive(agentStatusConfirm);
              setAgentStatusConfirm(null);
            }}
          >
            {agentStatusConfirm?.isActive ? "Confirm inactive" : "Confirm active"}
          </Button>
        </DialogActions>
      </Dialog>
      <Dialog
        open={addAgentOpen}
        onClose={closeAddAgentDialog}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Add Agent</DialogTitle>
        <DialogContent dividers>
          <Stack spacing={2} sx={{ pt: 1 }}>
            <TextField
              label="Agent name"
              value={newAgentName}
              onChange={(event) => setNewAgentName(event.target.value)}
              fullWidth
            />
            <TextField
              label="Agent email"
              value={newAgentEmail}
              onChange={(event) => setNewAgentEmail(event.target.value)}
              fullWidth
            />
            <TextField
              label="Phone"
              value={newAgentPhone}
              onChange={(event) => setNewAgentPhone(event.target.value)}
              fullWidth
            />
            <TextField
              label="Address"
              value={newAgentAddress}
              onChange={(event) => setNewAgentAddress(event.target.value)}
              fullWidth
            />
            <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
              <TextField
                label="Country"
                value={newAgentCountry}
                onChange={(event) => {
                  setNewAgentCountry(event.target.value);
                  setNewAgentState("");
                  setNewAgentCity("");
                }}
                select
                disabled={locationQuery.loading}
                helperText={locationQuery.loading ? "Loading countries..." : " "}
                fullWidth
              >
                {countries.map((item: any) => (
                  <MenuItem key={item.id} value={item.name}>
                    {item.name}
                  </MenuItem>
                ))}
              </TextField>
              <TextField
                label="State"
                value={newAgentState}
                onChange={(event) => {
                  setNewAgentState(event.target.value);
                  setNewAgentCity("");
                }}
                select
                disabled={!newAgentCountry || locationQuery.loading}
                helperText={
                  !newAgentCountry
                    ? "Select country first"
                    : availableNewAgentStates.length
                      ? " "
                      : "No states found for the selected country"
                }
                fullWidth
              >
                {availableNewAgentStates.map((item: any) => (
                  <MenuItem key={item.id} value={item.name}>
                    {item.name}
                  </MenuItem>
                ))}
              </TextField>
            </Stack>
            <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
              <TextField
                label="City"
                value={newAgentCity}
                onChange={(event) => setNewAgentCity(event.target.value)}
                select
                disabled={!newAgentCountry || !newAgentState || locationQuery.loading}
                helperText={
                  !newAgentCountry
                    ? "Select country first"
                    : !newAgentState
                      ? "Select state first"
                      : availableNewAgentCities.length
                        ? " "
                        : "No cities found for the selected state"
                }
                fullWidth
              >
                {availableNewAgentCities.map((item: any) => (
                  <MenuItem key={item.id} value={item.name}>
                    {item.name}
                  </MenuItem>
                ))}
              </TextField>
              <TextField
                label="Zip code"
                value={newAgentZipcode}
                onChange={(event) => setNewAgentZipcode(event.target.value)}
                fullWidth
              />
            </Stack>
            <TextField
              label="Optional custom temporary password"
              type="password"
              placeholder="Leave blank to auto-generate a random password"
              value={newAgentPassword}
              onChange={(event) => setNewAgentPassword(event.target.value)}
              fullWidth
            />
            <Typography variant="caption" color="text.secondary">
              Best option: leave this blank. Plany will generate and email a neutral random
              temporary password automatically. If this email already belongs to an existing user,
              we update that user instead of creating a duplicate account and email access
              instructions.
            </Typography>
            <FormControlLabel
              control={
                <Checkbox
                  checked={newAgentAdmin}
                  onChange={(event) => setNewAgentAdmin(event.target.checked)}
                />
              }
              label="Also grant platform admin access"
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeAddAgentDialog}>Cancel</Button>
          <Button variant="contained" onClick={submitNewAgent} disabled={creatingAgent}>
            Create agent
          </Button>
        </DialogActions>
      </Dialog>
      <Dialog
        open={Boolean(assignmentDialog)}
        onClose={closeAssignmentDialog}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          {assignmentDialog?.ownerAgentId ? "Reassign Request" : "Assign Request"}
        </DialogTitle>
        <DialogContent dividers>
          <Stack spacing={2}>
            <Typography variant="body2" color="text.secondary">
              {assignmentDialog?.subject || "Case"}
            </Typography>
            <TextField
              select
              label="Target agent"
              value={assignmentTargetId}
              onChange={(event) => setAssignmentTargetId(event.target.value)}
              fullWidth
            >
              {assignableAgents
                .filter((agent) => String(agent.id) !== String(assignmentDialog?.ownerAgentId || ""))
                .map((agent) => (
                  <MenuItem key={agent.id} value={agent.id}>
                    {agent.name}
                    {agent.email ? ` (${agent.email})` : ""}
                  </MenuItem>
                ))}
            </TextField>
            <TextField
              label="Assignment note"
              value={assignmentNote}
              onChange={(event) => setAssignmentNote(event.target.value)}
              multiline
              minRows={2}
              fullWidth
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeAssignmentDialog}>Cancel</Button>
          <Button
            variant="contained"
            onClick={submitAssignment}
            disabled={
              !assignmentTargetId ||
              transferringCase ||
              reassigningCase ||
              transferringGroup ||
              reassigningGroup
            }
          >
            Save
          </Button>
        </DialogActions>
      </Dialog>
      <Dialog
        open={Boolean(historyDialog)}
        onClose={() => setHistoryDialog(null)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>Assignment History</DialogTitle>
        <DialogContent dividers>
          <Stack spacing={2}>
            <Typography variant="subtitle1" fontWeight={700}>
              {historyDialog?.subject || "Case"}
            </Typography>
            <Box>
              <Typography variant="subtitle2" sx={{ mb: 1 }}>
                Assignments
              </Typography>
              <Stack spacing={1}>
                {(historyDialog?.history?.assignments || []).length ? (
                  (historyDialog?.history?.assignments || []).map((entry: any) => (
                    <Paper key={entry.id} variant="outlined" sx={{ p: 1.25 }}>
                      <Typography variant="body2" fontWeight={600}>
                        {entry.agentName || `Agent ${entry.agentId || "-"}`} • {entry.status}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        Assigned {formatDate(entry.assignedAt)}
                        {entry.fromAgentName ? ` • From ${entry.fromAgentName}` : ""}
                        {entry.endedAt ? ` • Ended ${formatDate(entry.endedAt)}` : ""}
                      </Typography>
                      {entry.note ? (
                        <Typography variant="body2" sx={{ mt: 0.5 }}>
                          {entry.note}
                        </Typography>
                      ) : null}
                    </Paper>
                  ))
                ) : (
                  <Typography variant="body2" color="text.secondary">
                    No assignment history yet.
                  </Typography>
                )}
              </Stack>
            </Box>
            <Box>
              <Typography variant="subtitle2" sx={{ mb: 1 }}>
                Activity
              </Typography>
              <Stack spacing={1}>
                {(historyDialog?.history?.activity || []).length ? (
                  (historyDialog?.history?.activity || []).map((entry: any) => (
                    <Paper key={entry.id} variant="outlined" sx={{ p: 1.25 }}>
                      <Typography variant="body2" fontWeight={600}>
                        {entry.type}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {formatDate(entry.createdAt)}
                        {entry.targetAgentName ? ` • ${entry.targetAgentName}` : ""}
                      </Typography>
                      {entry.note ? (
                        <Typography variant="body2" sx={{ mt: 0.5 }}>
                          {entry.note}
                        </Typography>
                      ) : null}
                    </Paper>
                  ))
                ) : (
                  <Typography variant="body2" color="text.secondary">
                    No activity yet.
                  </Typography>
                )}
              </Stack>
            </Box>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setHistoryDialog(null)}>Close</Button>
        </DialogActions>
      </Dialog>
      <Dialog
        open={Boolean(selectedAgent)}
        onClose={() => setSelectedAgent(null)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>Assigned Agent</DialogTitle>
        <DialogContent dividers>
          {selectedAgent && hasResolvedAgentIdentity(selectedAgent) ? (
            <Stack spacing={1.25}>
              <Typography variant="subtitle1" fontWeight={700}>
                {getAgentDisplayLabel(selectedAgent) || "Unknown agent"}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {selectedAgent?.userId ? `User ID ${selectedAgent.userId}` : "No user id"}
              </Typography>
              <Typography variant="body2">
                Email: {selectedAgent?.email || "Not available"}
              </Typography>
              <Typography variant="body2">
                Phone: {selectedAgent?.phone || "Not available"}
              </Typography>
              <Typography variant="body2">
                Location: {getAgentLocationLabel(selectedAgent)}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Routing profile: {isAgentProfileComplete(selectedAgent) ? "Ready" : "Needs update"}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Source: {selectedAgent?.source || "unknown"}
              </Typography>
            </Stack>
          ) : (
            <Typography variant="body2" color="text.secondary">
              Assigned agent details are not available for this case yet.
            </Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSelectedAgent(null)}>Close</Button>
        </DialogActions>
      </Dialog>
      <Dialog
        open={Boolean(applicationDialog)}
        onClose={() => {
          setApplicationDialog(null);
          setApplicationReviewNotes("");
          setApplicationTempPassword("");
        }}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          {applicationDialog?.decision === "APPROVED"
            ? "Approve Agent Application"
            : "Reject Agent Application"}
        </DialogTitle>
        <DialogContent dividers>
          <Stack spacing={2} sx={{ pt: 1 }}>
            <Typography variant="body2" color="text.secondary">
              {applicationDialog?.application?.username || "Applicant"} •{" "}
              {applicationDialog?.application?.email || "-"}
            </Typography>
            {applicationDialog?.application?.notes ? (
              <Alert severity="info">{applicationDialog.application.notes}</Alert>
            ) : null}
            <TextField
              label="Review notes"
              value={applicationReviewNotes}
              onChange={(event) => setApplicationReviewNotes(event.target.value)}
              multiline
              minRows={3}
              fullWidth
            />
            {applicationDialog?.decision === "APPROVED" ? (
              <>
                <TextField
                  label="Optional custom temporary password"
                  value={applicationTempPassword}
                  onChange={(event) => setApplicationTempPassword(event.target.value)}
                  type="password"
                  placeholder="Leave blank to auto-generate a random password"
                  fullWidth
                />
                <Typography variant="caption" color="text.secondary">
                  Approval upgrades or creates the user, enables agent access, and emails the
                  sign-in instructions automatically. Applications approved from this flow do not
                  receive platform admin access.
                </Typography>
              </>
            ) : null}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setApplicationDialog(null);
              setApplicationReviewNotes("");
              setApplicationTempPassword("");
            }}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            color={applicationDialog?.decision === "APPROVED" ? "success" : "warning"}
            disabled={reviewingApplication || !applicationDialog}
            onClick={async () => {
              if (!applicationDialog) return;
              setActionError(null);
              await reviewAgentApplication({
                variables: {
                  applicationId: applicationDialog.application.id,
                  decision: applicationDialog.decision,
                  reviewNotes: applicationReviewNotes.trim() || null,
                  password:
                    applicationDialog.decision === "APPROVED"
                      ? applicationTempPassword.trim() || null
                      : null,
                },
              });
            }}
          >
            {applicationDialog?.decision === "APPROVED"
              ? "Approve application"
              : "Reject application"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
